import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode, useCallback, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { avatarColorClass } from '@/lib/utils/avatar';
import { cn } from '@/lib/utils/cn';
import { UserMenuCloseContext } from './context';
import type { UserMenuSubject } from './types';

export type {
  UserMenuCompany,
  UserMenuCredits,
  UserMenuSubject,
} from './types';
export { UserMenuAiCredits } from './UserMenuAiCredits';
export { UserMenuCompanyCard } from './UserMenuCompanyCard';
export { UserMenuCompanySwitcher } from './UserMenuCompanySwitcher';
export { UserMenuItem } from './UserMenuItem';
export { UserMenuLink } from './UserMenuLink';
export { UserMenuSection } from './UserMenuSection';
export { UserMenuSeparator } from './UserMenuSeparator';
export { UserMenuUserCard } from './UserMenuUserCard';

interface UserMenuProps {
  /** Whom the trigger names — the company for a member, the person otherwise. */
  trigger: UserMenuSubject;
  children: ReactNode;
}

/**
 * Popover shell for the header's user menu: the trigger, the panel, and nothing
 * about what goes inside it.
 *
 * The contents are composed by the caller from the parts exported alongside this
 * — including the separators, which are placed explicitly rather than owned by
 * each block. A menu whose sections vary by area cannot have implicit rules
 * without eventually rendering two in a row or none at all; making them visible
 * in the composition is what stops that.
 */
export function UserMenu({ trigger, children }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant='ghost'
            className='h-7.5 gap-2 pl-0.75 pr-2 rounded-full'
          />
        }
      >
        {/* A company is a rounded square and a person a circle, as in
            `EntityAvatar`; the corner is its token for a 24px box. */}
        <Avatar
          className={cn(
            'h-6 w-6',
            trigger.kind === 'company' && 'rounded after:rounded',
          )}
        >
          <AvatarImage
            src={trigger.imageUrl}
            alt={trigger.name}
            className={cn(trigger.kind === 'company' && 'rounded')}
          />
          <AvatarFallback
            delay={0}
            className={cn(
              'text-xs font-semibold',
              avatarColorClass(trigger.colorSeed),
              trigger.kind === 'company' && 'rounded',
            )}
          >
            {trigger.initials}
          </AvatarFallback>
        </Avatar>
        <span className='text-sm font-medium max-w-36 truncate'>
          {trigger.name}
        </span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className='size-3 text-muted-foreground shrink-0'
        />
      </PopoverTrigger>

      <PopoverContent align='end' className='w-75 p-0 gap-0'>
        <UserMenuCloseContext.Provider value={close}>
          {children}
        </UserMenuCloseContext.Provider>
      </PopoverContent>
    </Popover>
  );
}
